import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

// Reusable UK postcode → address lookup. Provider-agnostic: talks only to our
// own /api/address/search. Emits a normalized address to onSelect:
//   { address_line_1, address_line_2, address_line_3, town_city, county, postcode, country, label }
// Manual entry is never blocked — on any failure we show a friendly message.
export default function AddressLookup({ onSelect, className = "" }) {
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [msg, setMsg] = useState("");

  const find = async () => {
    const pc = postcode.trim();
    if (pc.replace(/\s+/g, "").length < 5) { toast.error("Enter a full UK postcode, e.g. FY4 4XX"); return; }
    setLoading(true); setMsg(""); setResults(null);
    try {
      const r = await api.get("/address/search", { params: { postcode: pc } });
      const items = r.data.items || [];
      setResults(items);
      if (items.length === 0) setMsg(r.data.message || "No addresses found. Enter it manually below.");
    } catch (e) {
      setResults([]);
      setMsg(e.response?.data?.detail || "We couldn't retrieve addresses for this postcode. You can enter the address manually.");
    } finally { setLoading(false); }
  };

  const pick = (e) => {
    const idx = Number(e.target.value);
    if (Number.isNaN(idx) || !results || !results[idx]) return;
    onSelect(results[idx]);
    toast.success("Address filled in — you can still edit it below");
  };

  return (
    <div className={`rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 ${className}`} data-testid="address-lookup">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MapPin className="w-4 h-4 text-indigo-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); find(); } }}
            placeholder="Enter UK postcode, e.g. FY4 4XX"
            className="pl-8 h-9 bg-white"
            data-testid="address-lookup-postcode"
          />
        </div>
        <Button type="button" onClick={find} disabled={loading} className="h-9 shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="address-lookup-find">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find address
        </Button>
      </div>
      {results && results.length > 0 && (
        <select onChange={pick} defaultValue="" className="mt-2 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="address-lookup-select">
          <option value="" disabled>Select an address ({results.length} found)…</option>
          {results.map((a, i) => <option key={i} value={i}>{a.label || a.address_line_1}</option>)}
        </select>
      )}
      {msg && <p className="mt-2 text-xs text-amber-700" data-testid="address-lookup-msg">{msg}</p>}
    </div>
  );
}
